import React, { useCallback, useEffect, useState } from 'react';
import { Table, Pagination, type TableProps, Modal } from '@gamesb42/ui-kit';
import { Link, useHistory, useParams } from 'react-router-dom';
import queryString from 'query-string';
import moment from 'moment';

import TableWrapper from 'components/TableWrapper';
import { getTrancheAuditLog, getTrancheAuditLogRecord } from 'queries/tranche/getTrancheAuditLog';
import { TrancheAuditLogRecord, TrancheAuditLogResponse, ChangeHistoryRecord } from 'types/TrancheAuditLogTypes';

import s from './styles.module.scss';

interface QueryParams {
  page?: number;
  page_size?: number;
}

export default function TrancheAuditLog() {
  const [loading, setLoading] = useState(true);
  const [auditData, setAuditData] = useState<TrancheAuditLogResponse>({
    records: [],
    total: 0,
    page: 0,
    page_size: 25,
  });
  const [loading2, setLoading2] = useState(true);
  const [recordData, setRecordData] = useState<ChangeHistoryRecord[]>([]);
  const [recordId, setRecordId] = useState('');
  const [open, setOpen] = useState(false);
  const history = useHistory();
  //@ts-ignore
  const { workingCapitalId } = useParams();
  const query: QueryParams = queryString.parse(history.location.search, { parseNumbers: true });
  const { page_size = 25, page = 0 } = query;

  const columns: TableProps<TrancheAuditLogRecord>['columns'] = [
    {
      title: 'Date',
      align: 'left' as const,
      width: '200px',
      render: (_: any, record: TrancheAuditLogRecord) => {
        return moment(record.createdAt, moment.ISO_8601).format('DD.MM.YYYY, HH:mm');
      },
    },
    {
      title: 'Author',
      align: 'left' as const,
      render: (_: any, record: TrancheAuditLogRecord) => {
        return (
          <Link to={`/users/${record.userId}`} className={s.authorLink}>
            {record.userName}
          </Link>
        );
      },
    },
    {
      title: 'Section',
      dataIndex: 'objectName',
      align: 'left' as const,
    },
  ];

  const recordColumns = [
    {
      title: 'Before',
      align: 'left' as const,
      render: (_: any, record: ChangeHistoryRecord) => {
        return (
          <div>
            {record.key}: <span className={s.oldValue}>{JSON.stringify(record.old)}</span>
          </div>
        );
      },
    },
    {
      title: 'After',
      align: 'left' as const,
      render: (_: any, record: ChangeHistoryRecord) => {
        return (
          <div>
            {record.key}: <span className={s.newValue}>{JSON.stringify(record.new)}</span>
          </div>
        );
      },
    },
  ];

  const onChangeQuery = useCallback(
    (newValues: Partial<QueryParams>) => {
      history.replace(
        `${history.location.pathname}?${queryString.stringify(
          { page_size, page, ...newValues },
          { skipEmptyString: true },
        )}`,
      );
    },
    [history, page_size, page],
  );

  useEffect(() => {
    const fetchAuditData = async () => {
      if (!workingCapitalId) return;

      try {
        setLoading(true);
        const result = await getTrancheAuditLog(workingCapitalId, { page, page_size });
        setAuditData(result);
      } catch (error) {
        // Error handling is managed by baseRequest
      } finally {
        setLoading(false);
      }
    };

    fetchAuditData();
  }, [page_size, page, workingCapitalId]);

  useEffect(() => {
    if (recordId.length > 0) {
      const fetchRecordData = async () => {
        try {
          setLoading2(true);
          const result = await getTrancheAuditLogRecord(recordId);

          const preparedData: ChangeHistoryRecord[] = Object.entries(result.delta_flat || {}).map(([key, value]) => ({
            key,
            old: value.old,
            new: value.new,
          }));

          setRecordData(preparedData);
        } catch (error) {
          // Error handling is managed by baseRequest
        } finally {
          setLoading2(false);
        }
      };

      fetchRecordData();
    }
  }, [recordId]);

  return (
    <div className={s.container}>
      <TableWrapper>
        <Table
          loading={loading}
          onRow={(record) => ({
            onClick: () => {
              setRecordId(record.id);
              setOpen(true);
            },
          })}
          columns={columns}
          dataSource={auditData.records}
          rowKey="id"
          className={s.table}
        />
        <Pagination
          total={auditData.total}
          current={(auditData?.page || 0) + 1}
          pageSize={page_size as number}
          onChange={(newCurrent: number, newPageSize: number) =>
            onChangeQuery({ page: newCurrent - 1, page_size: newPageSize })
          }
        />
      </TableWrapper>
      <Modal
        open={open}
        title="Change history"
        onOk={() => setOpen(false)}
        okButtonProps={{
          onMouseDown: () => {},
        }}
        okText="OK"
        onCancel={() => {
          setOpen(false);
          setRecordId('');
          setRecordData([]);
        }}
        width="768px"
      >
        <Table
          loading={loading2}
          style={{ cursor: 'pointer' }}
          columns={recordColumns}
          dataSource={recordData}
          rowKey="key"
          className={s.modalTable}
        />
      </Modal>
    </div>
  );
}
